/**
 * `derivePhase` — pure function mapping query state → AI Eval render phase.
 *
 * Spec: `frontend/docs/30j_3_design.md` §3.1 + `frontend/docs/30j_3_architect.md` §3.
 *
 * Six phases: `no-jd` / `loading` / `empty` / `generating` / `loaded` / `error`.
 *
 * Defensive belts:
 *   - 404 surfaced via `query.isError` (hook contract regression) → empty,
 *     not error. The hook's queryFn catches 404 and returns `null`, so this
 *     branch should never fire in practice — but if a future bug regresses
 *     the contract, 404 still flows to the empty state.
 *   - `complete` row with null/whitespace `analysis` → empty (defensive
 *     belt against an upstream worker bug that left analysis blank).
 *   - Unknown status enum value → empty. Avoids rendering an error surface
 *     for a not-yet-recognized status that may simply be a future addition.
 */
import type { UseQueryResult } from "@tanstack/react-query";
import { ApiError } from "@/lib/apiError";
import type { InterviewAnalysisRow } from "@/hooks/queries/interview-analysis/useInterviewAnalysis";

export type EvalPhase =
  | "no-jd"
  | "loading"
  | "empty"
  | "generating"
  | "loaded"
  | "error";

interface DerivePhaseInput {
  jdId: string | undefined;
  query: UseQueryResult<InterviewAnalysisRow | null, ApiError>;
}

export function derivePhase({ jdId, query }: DerivePhaseInput): EvalPhase {
  if (!jdId) return "no-jd";

  // Skeleton until the query SETTLES. Gate on `isPending`
  // (status === "pending" ⇒ no data yet), NOT `isLoading`/`isFetching`.
  // `isLoading` is `isPending && isFetching`, so it is FALSE for a
  // disabled-but-mounted query and for the single render where `enabled`
  // has just flipped true but `fetchStatus` is still "idle" (the fetch is
  // scheduled in a post-render effect). In those frames `data` is still
  // undefined, and the old `isLoading` gate fell through to `"empty"` —
  // flashing the Generate CTA for a frame before an existing analysis
  // lands. `isPending` keeps us on the skeleton until the query resolves.
  // Mirrors the CandidatesV2 loading gate (founder bug FIX 1).
  if (query.isPending) return "loading";

  if (query.isError) {
    // Defensive belt: hook should turn 404 → null, but if it regresses
    // and a 404 reaches isError, treat as empty NOT error.
    if (query.error instanceof ApiError && query.error.status === 404) {
      return "empty";
    }
    return "error";
  }

  const row = query.data;
  if (!row) return "empty"; // canonical 404-via-null path

  if (row.status === "queued" || row.status === "running") return "generating";
  if (row.status === "failed") return "error";
  if (row.status === "complete") {
    if (
      typeof row.analysis === "string" &&
      row.analysis.trim().length > 0
    ) {
      return "loaded";
    }
    // Defensive: complete + null/whitespace analysis → empty.
    return "empty";
  }

  // Unknown status enum value — defensive empty.
  return "empty";
}
